/**
 * @file consent-cookie.ts
 * @description Cookie выбора посетителя `commora_consent` (phase19.16, партия
 * Р3г, v0.19.62; site-builder.md SB135, SB136; db-design-r3g §6.1;
 * integrations-r3g-counters §4; поправка оркестратора 2026-10-23 — cookie
 * ставится ответом сервера витрины, запись из скрипта — запасной путь).
 *
 * Зачем в shared: формат значения, сроки и «действует ли выбор» нужны
 * обработчику маршрута витрины (Set-Cookie ответом сервера — иначе Safari ITP
 * режет срок cookie из `document.cookie` до 7 дней), клиентскому баннеру и
 * загрузчику счётчиков. Разбор — чистые функции, время — параметром.
 *
 * Формат значения: `<ключ условий>.<категории>.<unix-секунды>`, например
 * `e1v42c.am.1760947200`. Категории: `a` — аналитика, `m` — реклама (буква
 * формата из проекта данных, «marketing»), `am` — обе, `-` — ни одной.
 * Номеров счётчиков и персональных данных в значении нет. Cookie без
 * `Domain` (на каждом домене магазина согласие своё), `Path=/`,
 * `SameSite=Lax`, `Secure` на https, без `HttpOnly` — её читает клиент.
 * Сама cookie строго необходимая, согласия не требует (SB135).
 *
 * Прежний ключ браузера `chaiberi_cookie_consent_v1` засчитывается только как
 * «Понятно» без счётчиков и согласием на аналитику не считается (SB135);
 * чтение убрать через 12 месяцев (TD-509).
 */

import type { SiteConsentCategory } from "../site-theme/counters.js";
import type { SiteConsentBannerKind } from "./consent-texts.js";

/** Имя cookie выбора посетителя. */
export const SITE_CONSENT_COOKIE = "commora_consent";

/** Прежний ключ localStorage (CookieBanner.tsx до Р3г). Убрать чтение после 2027-11 (TD-509). */
export const LEGACY_CONSENT_STORAGE_KEY = "chaiberi_cookie_consent_v1";

/** Сроки повторного показа (SB136; на проверку юристу, OQ-123): 12 месяцев после «Принять», 6 — после отказа. */
export const SITE_CONSENT_KEEP_DAYS = Object.freeze({ accepted: 365, refused: 182 } as const);

const DAY_SEC = 86_400;

/** Те же сроки в секундах для `Max-Age`: 31 536 000 и 15 724 800. */
export const SITE_CONSENT_MAX_AGE_SEC = Object.freeze({
  accepted: SITE_CONSENT_KEEP_DAYS.accepted * DAY_SEC,
  refused: SITE_CONSENT_KEEP_DAYS.refused * DAY_SEC,
} as const);

/** Выбор по категориям. */
export type SiteConsentChoice = Readonly<Record<SiteConsentCategory, boolean>>;

/** Ничего не разрешено (и «Только необходимые»). */
export const SITE_CONSENT_NONE: SiteConsentChoice = Object.freeze({ analytics: false, ads: false });
/** Всё разрешено («Принять все», «Понятно» в режиме «Уведомление»). */
export const SITE_CONSENT_ALL: SiteConsentChoice = Object.freeze({ analytics: true, ads: true });

/** Разобранное значение cookie. */
export interface SiteConsentCookieValue {
  /** Ключ условий, при которых сделан выбор (`PublicSiteConsentBannerDto.key`). */
  key: string;
  choice: SiteConsentChoice;
  /** Когда сделан выбор, unix-секунды. */
  at: number;
}

/** Буквы категорий в значении. */
const CATEGORY_LETTER: Readonly<Record<SiteConsentCategory, string>> = { analytics: "a", ads: "m" };

/** Ключ условий: латиница и цифры (сейчас `e1v42c`), не длиннее 32 знаков. */
const KEY_PATTERN = /^[A-Za-z0-9]{1,32}$/u;
const CATS_PATTERN = /^(?:-|a|m|am)$/u;
const AT_PATTERN = /^\d{1,12}$/u;

function encodeChoice(choice: SiteConsentChoice): string {
  const letters = `${choice.analytics ? CATEGORY_LETTER.analytics : ""}${choice.ads ? CATEGORY_LETTER.ads : ""}`;
  return letters === "" ? "-" : letters;
}

/** Значение cookie: `e1v42c.am.1760947200`. Время — параметром (`Date`), секунды вниз. */
export function encodeConsentCookie(input: { key: string; choice: SiteConsentChoice; at: Date }): string {
  if (!KEY_PATTERN.test(input.key)) throw new Error(`site_consent_key_invalid: ${input.key}`);
  return `${input.key}.${encodeChoice(input.choice)}.${Math.floor(input.at.getTime() / 1000)}`;
}

/**
 * Разбор значения cookie. Мусор, чужой формат, пустая строка — null
 * («согласия нет»): лучше спросить ещё раз, чем включить счётчик по
 * непонятной записи. Значение может прийти URL-кодированным.
 */
export function parseConsentCookie(raw: string | null | undefined): SiteConsentCookieValue | null {
  if (typeof raw !== "string" || raw === "") return null;
  let value = raw.trim();
  try {
    value = decodeURIComponent(value);
  } catch {
    return null;
  }
  const parts = value.split(".");
  if (parts.length !== 3) return null;
  const [key, cats, at] = parts as [string, string, string];
  if (!KEY_PATTERN.test(key) || !CATS_PATTERN.test(cats) || !AT_PATTERN.test(at)) return null;
  return {
    key,
    choice: { analytics: cats.includes(CATEGORY_LETTER.analytics), ads: cats.includes(CATEGORY_LETTER.ads) },
    at: Number(at),
  };
}

/** Значение cookie из заголовка `Cookie` или `document.cookie`; нет — null. */
export function readConsentCookieFromHeader(cookieHeader: string | null | undefined): SiteConsentCookieValue | null {
  if (typeof cookieHeader !== "string") return null;
  for (const pair of cookieHeader.split(";")) {
    const eq = pair.indexOf("=");
    if (eq === -1) continue;
    if (pair.slice(0, eq).trim() === SITE_CONSENT_COOKIE) return parseConsentCookie(pair.slice(eq + 1));
  }
  return null;
}

/** Разрешена ли хоть одна категория. */
export function consentChoiceAcceptsAny(choice: SiteConsentChoice): boolean {
  return choice.analytics || choice.ads;
}

/**
 * Срок cookie, секунды: 12 месяцев, если принята хоть одна категория, иначе
 * 6. Уведомление без счётчиков («Понятно» у `info`) — 12 месяцев.
 */
export function consentCookieMaxAgeSec(kind: SiteConsentBannerKind, choice: SiteConsentChoice): number {
  return kind === "info" || consentChoiceAcceptsAny(choice)
    ? SITE_CONSENT_MAX_AGE_SEC.accepted
    : SITE_CONSENT_MAX_AGE_SEC.refused;
}

/**
 * Строка cookie для `Set-Cookie` (ответ сервера витрины) и для запасной
 * записи `document.cookie`: без `Domain` и `HttpOnly`, `Max-Age` вместо
 * `Expires` (Chrome режет сроки больше 400 дней — у нас меньше).
 */
export function buildConsentCookieString(value: string, opts: { maxAgeSec: number; secure: boolean }): string {
  const parts = [`${SITE_CONSENT_COOKIE}=${value}`, "Path=/", `Max-Age=${Math.max(0, Math.floor(opts.maxAgeSec))}`, "SameSite=Lax"];
  if (opts.secure) parts.push("Secure");
  return parts.join("; ");
}

/**
 * Действует ли выбор при текущих условиях: ключ совпадает с ключом баннера и
 * срок не вышел (cookie сама истекает по `Max-Age`; проверка времени — на
 * случай, если браузер продлил её или часы сдвинулись). Выбор «из будущего»
 * больше чем на сутки — не действует.
 */
export function isConsentCookieCurrent(
  value: SiteConsentCookieValue | null,
  banner: { key: string; kind: SiteConsentBannerKind },
  now: Date,
): boolean {
  if (value === null || value.key !== banner.key) return false;
  const nowSec = Math.floor(now.getTime() / 1000);
  if (value.at > nowSec + DAY_SEC) return false;
  return value.at + consentCookieMaxAgeSec(banner.kind, value.choice) > nowSec;
}

/** Итог для витрины: показывать ли баннер и какие категории разрешены прямо сейчас. */
export interface SiteConsentState {
  showBanner: boolean;
  allowed: SiteConsentChoice;
}

/**
 * Что делать на странице (db-design-r3g §2.2, §6.1):
 *   - выбор действует → баннера нет, разрешено по выбору;
 *   - `consent` без выбора → баннер, ничего не грузим;
 *   - `notice` без выбора → баннер, счётчики работают сразу (SB133);
 *   - `info` без выбора → баннер, счётчиков нет;
 *   - баннера нет (счётчиков нет и уведомление выключено) → ничего.
 * Исключённые адреса (ЛК, подтверждение заказа, предпросмотр) витрина
 * проверяет отдельно (`isCountersAllowedPath`).
 */
export function siteConsentState(
  banner: { key: string; kind: SiteConsentBannerKind } | null,
  value: SiteConsentCookieValue | null,
  now: Date,
): SiteConsentState {
  if (banner === null) return { showBanner: false, allowed: SITE_CONSENT_NONE };
  if (isConsentCookieCurrent(value, banner, now)) {
    return { showBanner: false, allowed: banner.kind === "info" ? SITE_CONSENT_NONE : value!.choice };
  }
  return { showBanner: true, allowed: banner.kind === "notice" ? SITE_CONSENT_ALL : SITE_CONSENT_NONE };
}

/**
 * Прежнее «Принять» (`chaiberi_cookie_consent_v1`, любое непустое значение):
 * засчитывается только как «Понятно» у уведомления без счётчиков (SB135).
 * Возвращает выбор, который надо записать в cookie (а прежний ключ —
 * удалить); для `consent` и `notice` — null: согласием на аналитику и
 * рекламу прежняя запись не считается.
 */
export function legacyConsentAsCookie(
  legacyValue: string | null | undefined,
  banner: { key: string; kind: SiteConsentBannerKind } | null,
): { key: string; choice: SiteConsentChoice } | null {
  if (banner === null || banner.kind !== "info") return null;
  if (typeof legacyValue !== "string" || legacyValue.trim() === "") return null;
  return { key: banner.key, choice: SITE_CONSENT_NONE };
}
